import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { servicesData } from '../data/mock';
import { Pen, Calculator, Zap, Printer, Cog, Factory, Scan, Award, Package, ArrowRight, X, ChevronDown, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';

const iconMap = {
  Pen: Pen,
  Calculator: Calculator,
  Zap: Zap,
  Printer: Printer,
  Cog: Cog,
  Factory: Factory,
  Scan: Scan,
  Award: Award,
  Package: Package
};

export default function Services() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [expandedId, setExpandedId] = useState(null);

  const toggleService = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    setExpandedId(null);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: 'easeOut' }
    }
  };

  return (
    <section ref={ref} className="relative py-32 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#4A6FA5]/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-[#2A4A85]/10 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <div className="text-[#6B8FCC]/70 font-mono text-sm mb-4 tracking-widest">SERVICES</div>
          <h2 className="text-5xl md:text-7xl font-black mb-6 bg-gradient-to-r from-white via-[#B8CDE8] to-white bg-clip-text text-transparent">
            Complete Product
            <br />
            Development Solutions
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            From initial concept to mass production, we handle every stage of your product journey
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto"
        >
          {servicesData.map((service) => {
            const Icon = iconMap[service.icon] || Pen;
            const isExpanded = expandedId === service.id;

            return (
              <motion.div
                key={service.id}
                variants={cardVariants}
                layout
                className={`group relative ${isExpanded ? 'md:col-span-2 lg:col-span-3' : ''}`}
                data-testid={`service-card-${service.id}`}
              >
                {/* Glass Card */}
                <div className="relative h-full p-8 rounded-3xl bg-white/5 backdrop-blur-xl border border-white/10 hover:border-[#4A6FA5]/50 transition-all duration-500 hover:bg-white/[0.07] overflow-hidden">
                  {/* Gradient Background on Hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#4A6FA5]/0 via-[#2A4A85]/0 to-[#4A6FA5]/0 group-hover:from-[#4A6FA5]/10 group-hover:via-[#2A4A85]/10 group-hover:to-[#4A6FA5]/10 transition-all duration-700 pointer-events-none" />

                  <div className={`relative z-10 ${isExpanded ? 'grid lg:grid-cols-2 gap-8' : ''}`}>
                    {/* Left Side (Card Content) */}
                    <div>
                      {/* Icon */}
                      <div className="mb-6">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#4A6FA5]/20 to-[#2A4A85]/20 flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 border border-[#4A6FA5]/30">
                          <Icon className="w-8 h-8 text-[#6B8FCC]" />
                        </div>
                      </div>

                      {/* Content */}
                      <h3 className="text-2xl font-bold mb-3 text-white group-hover:text-[#6B8FCC] transition-colors duration-300">
                        {service.title}
                      </h3>
                      <p className="text-gray-400 mb-6 leading-relaxed">
                        {service.description}
                      </p>

                      {/* Features List */}
                      <ul className="space-y-2 mb-6">
                        {service.features.slice(0, isExpanded ? service.features.length : 3).map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-gray-400">
                            <div className="w-1.5 h-1.5 bg-[#6B8FCC] rounded-full flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>

                      {/* Learn More / Collapse Button */}
                      <button
                        onClick={() => toggleService(service.id)}
                        data-testid={`service-toggle-${service.id}`}
                        className="flex items-center gap-2 text-[#6B8FCC] hover:text-[#8FAEE0] font-medium group/btn transition-all duration-300"
                      >
                        <span className="text-sm">{isExpanded ? 'Show Less' : 'Learn More'}</span>
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 rotate-180 transition-transform" />
                        ) : (
                          <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                        )}
                      </button>
                    </div>

                    {/* Right Side (Expanded Details) */}
                    <AnimatePresence>
                      {isExpanded && service.details && (
                        <motion.div
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ duration: 0.4, delay: 0.1 }}
                          className="space-y-6"
                          data-testid={`service-details-${service.id}`}
                        >
                          {/* Overview */}
                          <div>
                            <h4 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                              <span className="w-1 h-6 bg-gradient-to-b from-[#6B8FCC] to-[#4A6FA5] rounded-full" />
                              Overview
                            </h4>
                            <p className="text-gray-400 text-sm leading-relaxed">
                              {service.details.overview}
                            </p>
                          </div>

                          {/* Key Features */}
                          <div>
                            <h4 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                              <span className="w-1 h-6 bg-gradient-to-b from-[#6B8FCC] to-[#4A6FA5] rounded-full" />
                              Key Features
                            </h4>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {service.details.keyFeatures.map((feature, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-sm text-gray-400">
                                  <CheckCircle2 className="w-4 h-4 text-[#6B8FCC] flex-shrink-0 mt-0.5" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Applications & Benefits */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <h4 className="text-sm font-bold text-[#8FAEE0] mb-2 uppercase tracking-wider">Applications</h4>
                              <ul className="space-y-1">
                                {service.details.applications.map((app, idx) => (
                                  <li key={idx} className="text-sm text-gray-400 flex items-center gap-2">
                                    <div className="w-1 h-1 bg-[#6B8FCC] rounded-full" />
                                    {app}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <h4 className="text-sm font-bold text-[#8FAEE0] mb-2 uppercase tracking-wider">Benefits</h4>
                              <ul className="space-y-1">
                                {service.details.benefits.map((benefit, idx) => (
                                  <li key={idx} className="text-sm text-gray-400 flex items-center gap-2">
                                    <div className="w-1 h-1 bg-[#6B8FCC] rounded-full" />
                                    {benefit}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>

                          {/* Industries Served */}
                          <div>
                            <h4 className="text-sm font-bold text-[#8FAEE0] mb-2 uppercase tracking-wider">Industries Served</h4>
                            <div className="flex flex-wrap gap-2">
                              {service.details.industries.map((industry, idx) => (
                                <span
                                  key={idx}
                                  className="px-3 py-1 text-xs font-medium rounded-full bg-[#4A6FA5]/10 border border-[#4A6FA5]/30 text-[#8FAEE0]"
                                >
                                  {industry}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* CTA */}
                          <Button
                            onClick={scrollToContact}
                            data-testid={`service-cta-${service.id}`}
                            className="w-full sm:w-auto px-6 py-5 bg-gradient-to-r from-[#1E3A6F] to-[#2A4A85] hover:from-[#2A4A85] hover:to-[#4A6FA5] text-white border-0 rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-blue-900/50"
                          >
                            <span className="flex items-center gap-2">
                              Request a Quote
                              <ArrowRight className="w-4 h-4" />
                            </span>
                          </Button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Corner Decoration */}
                  {!isExpanded && (
                    <div className="absolute bottom-0 right-0 w-32 h-32 opacity-10 group-hover:opacity-20 transition-opacity duration-500 pointer-events-none">
                      <Icon className="w-full h-full text-[#6B8FCC]" />
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
